import { HighlightIcon, Tag } from '@dev-spendesk/grapes';
import { type ReactNode, useState } from 'react';

import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { SupplierLogo } from 'src/core/common/components/SupplierLogo/SupplierLogo';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { type BudgetDefinition } from 'src/core/modules/budgets/budgets-by-dimensions/hooks/budgetDefinition';
import { getDimensionIcon } from 'src/core/modules/budgets/budgets-by-dimensions/models/dimension';
import { BudgetPeriodTags } from 'src/core/modules/budgets/components/BudgetPeriodTags';
import { type PeriodRange } from 'src/core/modules/budgets/models/periodRange';

import { BudgetActionsDropdown } from './BudgetActionsDropdown';
import { BudgetRow } from '../../v1/BudgetCard/BudgetRow';
import { BudgetPeriodSelector } from '../../v1/BudgetPeriodSelector';
import { flattenBudgetTree, type FlattenedBudgetRow } from '../helpers/misc';
import { makePeriod } from '../helpers/period';
import { useBreakdownsWithDimensionData } from '../hooks/useBreakdownsWithDimensionData';

export type BudgetCardV2Props = {
  budgetDefinition: BudgetDefinition;
  selectedPeriodRange: PeriodRange;
  onSelectedPeriodRangeChange: (range: PeriodRange) => void;
  onRowClick: (rowId: string) => void;
};

/**
 * BudgetCardV2 - Budget card component for V2 budgets
 *
 * This component accepts a budget definition and internally fetches
 * breakdown data using the V2 API (useGetBudgetBreakdowns hook).
 * It transforms the V2 breakdown structure to the legacy format
 * and displays recursive rows for each dimensional breakdown.
 */
export const BudgetCardV2 = ({
  budgetDefinition,
  selectedPeriodRange,
  onSelectedPeriodRangeChange,
  onRowClick,
}: BudgetCardV2Props) => {
  const { t } = useTranslation('global');

  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());

  const breakdownsDataQueryState = useBreakdownsWithDimensionData(
    budgetDefinition.id,
    makePeriod(budgetDefinition.period, selectedPeriodRange),
  );

  const breakdownsData = unwrapQuery(breakdownsDataQueryState);
  if (!breakdownsData) {
    return null;
  }
  const { breakdownsResponse, dimensionData } = breakdownsData;

  const flattenedRows = flattenBudgetTree(
    breakdownsResponse.breakdowns,
    dimensionData,
    {
      budgetName: budgetDefinition.name,
      collapsedIds,
    },
  );

  const toggleCollapse = (rowId: string) => {
    setCollapsedIds((previous) => {
      const next = new Set(previous);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const getCollapse = (row: FlattenedBudgetRow) => {
    if (row.hasChildren) {
      return {
        isCollapsed: collapsedIds.has(row.id),
        onToogleCollapse: () => toggleCollapse(row.id),
      };
    }
  };

  const getRowIcon = (row: FlattenedBudgetRow): ReactNode | undefined => {
    if (row.dimension === 'root') {
      return;
    }
    if (row.dimension === 'supplier') {
      const logoUrl = dimensionData.supplier.get(row.id)?.thumbnailUrl;
      if (logoUrl) {
        return <SupplierLogo name={row.label} src={logoUrl} size={32} />;
      }
    }
    return <HighlightIcon size={32} {...getDimensionIcon(row.dimension)} />;
  };

  return (
    <div className="box p-0 pb-8">
      <div className="flex w-full items-center justify-between gap-16 px-16 py-12">
        <div className="flex items-center gap-16">
          <span className="title-m text-content-primary">
            {budgetDefinition.name}
          </span>
          <Tag variant="pink">{t('budget.redesigned.budgetV2Tag')}</Tag>
          <BudgetPeriodTags period={budgetDefinition.period} />
        </div>

        <div className="flex items-center gap-8">
          <BudgetPeriodSelector
            range={selectedPeriodRange}
            maxRange={{
              from: budgetDefinition.period.start,
              to: budgetDefinition.period.end,
            }}
            onChange={onSelectedPeriodRangeChange}
          />
          <BudgetActionsDropdown
            budgetDefinitionId={budgetDefinition.id}
            companyId={budgetDefinition.companyId}
          />
        </div>
      </div>

      <div className="mb-16 grid w-full grid-cols-4 px-16 pt-16 pl-[54px]">
        <div className="text-content-secondary-bg-primary body-m -ml-40">
          {t('budget.redesigned.budget')}
        </div>
        <div className="flex justify-between">
          <div className="text-content-secondary-bg-primary body-m">
            {t('budget.redesigned.utilization')}
          </div>
          <div className="text-content-secondary-bg-primary body-m">
            {t('budget.redesigned.total')}
          </div>
        </div>
        <div className="text-content-secondary-bg-primary body-m text-right">
          {t('budget.redesigned.committed')}
        </div>
        <div className="text-content-secondary-bg-primary body-m text-right">
          {t('budget.redesigned.available')}
        </div>
      </div>

      {flattenedRows.map((row) => (
        <div key={row.id} className="px-8">
          <BudgetRow
            rowName={row.label}
            breakdown={row.breakdown}
            onRowClick={() => onRowClick(row.id)}
            rowIcon={getRowIcon(row)}
            collapse={getCollapse(row)}
            depth={row.depth}
            className="py-8"
          />
        </div>
      ))}
    </div>
  );
};
